/**
 * 高级 ProTable 组件
 * 支持自动数据加载、搜索、分页等功能
 */

import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useOnPageReturn } from '@/hooks/usePageTabActive';
import {
  Table,
  Card,
  Space,
  Button,
  Row,
  Col,
  TableProps,
  TableColumnProps,
  message,
} from 'antd';

export interface ProTableColumn<T = object> extends Omit<TableColumnProps<T>, 'dataIndex'> {
  dataIndex?: keyof T;
  hideInTable?: boolean;
  hideInSearch?: boolean;
  searchConfig?: {
    type?: 'input' | 'select' | 'date' | 'dateRange' | 'number';
    options?: Array<{ label: string; value: unknown }>;
    placeholder?: string;
  };
}

export interface ProTableInstance {
  reload: () => void;
  reset: () => void;
}

export interface ProTableProps<T = object> {
  columns: ProTableColumn<T>[];
  rowKey: string | ((record: T) => string);
  request?: (params: { page: number; per_page: number; [key: string]: unknown }) => Promise<{
    data: T[];
    total: number;
    success: boolean;
  }>;
  params?: object;
  dataSource?: T[];
  loading?: boolean;
  scroll?: { x?: number | string; y?: number | string };
  rowSelection?: TableProps<T>['rowSelection'];

  // 工具栏配置
  toolbar?: {
    title?: string;
    actions?: React.ReactNode[];
    settings?: Array<{
      key: string;
      icon: React.ReactNode;
      tooltip: string;
      onClick: () => void;
    }>;
  };

  // 批量操作
  batchActions?: React.ReactNode[];

  // REQ-20260619-0005: 嵌入模式——不渲染自带 Card 外壳，由父级提供单层容器（去 wrapper 嵌套）。默认 false，不影响既有用法。
  embedded?: boolean;

  // 其他 Table 属性
  tableProps?: Omit<TableProps<T>, 'columns' | 'dataSource' | 'loading' | 'pagination' | 'rowKey'>;
}

const EMPTY_PARAMS: object = {};

const AdvancedProTable = <T extends object>(
  props: ProTableProps<T>,
  ref: React.Ref<ProTableInstance>
) => {
  const {
    columns,
    rowKey,
    request,
    params = EMPTY_PARAMS,
    dataSource: externalDataSource,
    loading: externalLoading = false,
    scroll,
    rowSelection,
    toolbar,
    batchActions,
    embedded = false,
    tableProps,
  } = props;

  const [dataSource, setDataSource] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} 共 ${total} 条`,
  });
  // REQ-20260619-0005: 当前排序（用 ref 避免触发 loadData 依赖循环）
  const sorterRef = React.useRef<{ sort_by?: string; sort_order?: 'asc' | 'desc' }>({});

  const { current: currentPage, pageSize: currentPageSize } = pagination;

  // 加载数据
  const loadData = useCallback(
    async (page = currentPage, pageSize = currentPageSize) => {
      if (!request) return;

      setLoading(true);
      try {
        const requestParams = {
          page,
          per_page: pageSize,
          ...params,
          ...sorterRef.current,
        };

        const response = await request(requestParams);

        if (response.success) {
          setDataSource(response.data || []);
          setPagination(prev => ({
            ...prev,
            current: page,
            pageSize,
            total: response.total || 0,
          }));
        } else {
          message.error('数据加载失败');
        }
      } catch (error) {
        console.error('ProTable loadData error:', error);
        message.error('数据加载失败');
      } finally {
        setLoading(false);
      }
    },
    [request, params, currentPage, currentPageSize]
  );

  // 初始化加载数据。只跟着 params 重新查：调用方的 request 常是每次渲染新建的函数，跟着它会无限循环
  const loadDataRef = React.useRef(loadData);
  loadDataRef.current = loadData;
  useEffect(() => {
    loadDataRef.current(1);
  }, [params]);

  // 处理分页 / 排序变化
  const handleTableChange = useCallback(
    (
      paginationConfig: { current?: number; pageSize?: number },
      _filters: unknown,
      sorter:
        | { field?: React.Key; columnKey?: React.Key; order?: 'ascend' | 'descend' | null }
        | Array<{ field?: React.Key; columnKey?: React.Key; order?: 'ascend' | 'descend' | null }>
    ) => {
      // REQ-20260619-0005: 捕获列排序 → sort_by/sort_order 透传给 request
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      if (s && s.order) {
        sorterRef.current = {
          sort_by: String(s.field ?? s.columnKey ?? ''),
          sort_order: s.order === 'ascend' ? 'asc' : 'desc',
        };
      } else {
        sorterRef.current = {};
      }

      const nextPage = paginationConfig.current ?? currentPage;
      const nextPageSize = paginationConfig.pageSize ?? currentPageSize;
      setPagination(prev => ({
        ...prev,
        current: nextPage,
        pageSize: nextPageSize,
      }));

      // 直接使用本次事件的页码，避免读取到更新前的 pagination 闭包。
      if (request) {
        loadData(nextPage, nextPageSize);
      }
    },
    [loadData, currentPage, currentPageSize, request]
  );

  // 刷新数据
  const reload = useCallback(() => {
    if (request) {
      loadData();
    }
  }, [loadData, request]);

  // 多标签页下从详情、编辑页返回时，按原来的页码和筛选重新查一次
  useOnPageReturn(reload);

  // 重置并刷新
  const reset = useCallback(() => {
    if (request) {
      loadData(1);
    }
  }, [loadData, request]);

  // 暴露实例方法
  useImperativeHandle(
    ref,
    () => ({
      reload,
      reset,
    }),
    [reload, reset]
  );

  // 渲染工具栏
  const renderToolbar = () => {
    if (!toolbar) return null;

    return (
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            {toolbar.title && <h3 style={{ margin: 0 }}>{toolbar.title}</h3>}
            {toolbar.actions}
          </Space>
        </Col>
        <Col>
          <Space>
            {toolbar.settings?.map(setting => (
              <Button
                key={setting.key}
                type="text"
                icon={setting.icon}
                title={setting.tooltip}
                onClick={setting.onClick}
              />
            ))}
          </Space>
        </Col>
      </Row>
    );
  };

  // 渲染批量操作
  const renderBatchActions = () => {
    if (!batchActions || !Array.isArray(batchActions) || batchActions.length === 0) {
      return null;
    }

    return (
      <div style={{ marginBottom: 16 }}>
        <Space>{batchActions}</Space>
      </div>
    );
  };

  // 使用外部数据源或内部数据源
  const finalDataSource = externalDataSource || dataSource;
  const finalLoading = externalLoading || loading;

  const inner = (
    <>
      {renderToolbar()}
      {renderBatchActions()}

      <Table<T>
        {...tableProps}
        columns={columns as TableProps<T>['columns']}
        dataSource={finalDataSource}
        loading={finalLoading}
        rowKey={rowKey}
        scroll={scroll}
        rowSelection={rowSelection}
        pagination={request ? pagination : false}
        onChange={handleTableChange}
      />
    </>
  );

  // REQ-20260619-0005: embedded 模式不包 Card，由父级单层容器承载
  return embedded ? inner : <Card>{inner}</Card>;
};

export default forwardRef(AdvancedProTable) as <T extends object>(
  props: ProTableProps<T> & { ref?: React.Ref<ProTableInstance> }
) => React.ReactElement;
